import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Alert,
  Divider,
  LinearProgress,
  Chip,
} from "@mui/material";
import { DataUsage, Security, Notifications, Info } from "@mui/icons-material";
import { BillingCard } from "../components/settings";
import ProfileInfo from "../components/settings/ProfileInfo";
import { ChangePasswordCard } from "../components/userProfile";
import { SPACING } from "../theme/constants";
import { fetchMyPlan } from "../redux/slices/planSlice";
import { changeUserPassword } from "../redux/slices/userSlice";

const Settings = () => {
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector((state) => state.user);
  const { myPlan, usage } = useSelector((state) => state.plan);

  const [successMessage, setSuccessMessage] = useState("");

  const handleChangePassword = async (data) => {
    try {
      await dispatch(changeUserPassword(data)).unwrap();
      setSuccessMessage("Password changed successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    dispatch(fetchMyPlan());
  }, [dispatch]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your account settings and preferences
          </Typography>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: SPACING.md, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={SPACING.md}>
        {/* Profile Settings */}
        <ProfileInfo user={user} loading={loading} error={error} />

        {/* Plan & Usage */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2.5,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <DataUsage sx={{ color: "primary.main" }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Plan & Usage
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Current Plan:
              </Typography>
              <Chip
                label={myPlan?.name || user?.plan?.name || "Free"}
                color={
                  (myPlan?.name || user?.plan?.name) === "Free"
                    ? "default"
                    : "success"
                }
                size="small"
              />
            </Box>
            {usage && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      Sites: {usage.sitesUsed} / {usage.sitesLimit}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usage.sitesPercentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usage.sitesPercentage, 100)}
                    color={
                      usage.sitesPercentage >= 90
                        ? "error"
                        : usage.sitesPercentage >= 70
                          ? "warning"
                          : "primary"
                    }
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="body2">
                      Storage: {usage.storageUsedGB} GB / {usage.storageLimitGB}{" "}
                      GB
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usage.storagePercentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usage.storagePercentage, 100)}
                    color={
                      usage.storagePercentage >= 90
                        ? "error"
                        : usage.storagePercentage >= 70
                          ? "warning"
                          : "primary"
                    }
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>

        {/* Password */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ChangePasswordCard onChangePassword={handleChangePassword} />
        </Grid>

        {/* Security */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2.5,
              height: "100%",
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Security sx={{ color: "primary.main" }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Security
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                py: 4,
              }}
            >
              <Info sx={{ fontSize: 40, color: "action.disabled" }} />
              <Typography variant="body1" fontWeight={500}>
                Advanced security settings coming soon
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Two-factor authentication, session management, and login history
                will be available in a future update.
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Notifications */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2.5,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Notifications sx={{ color: "primary.main" }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Notifications
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                py: 4,
              }}
            >
              <Info sx={{ fontSize: 40, color: "action.disabled" }} />
              <Typography variant="body1" fontWeight={500}>
                Notification preferences coming soon
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Email and SMS notification settings will be configurable in a
                future update.
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Billing */}
        <Grid size={{ xs: 12 }}>
          <BillingCard />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
